import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    CheckboxElement,
    FormContainer,
    SelectElement,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
} from '@mui/material';

import { CREATE_INGREDIENT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Ingredient } from '@barboxx/models';
import { INGREDIENT_CATEGORIES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateIngredientDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<Ingredient.CreateRequest>({
        resolver: yupResolver(CREATE_INGREDIENT_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateIngredient = useMutation({
        mutationKey: ['ingredient'],
        mutationFn: (data: Ingredient.CreateRequest) =>
            Api.Core.Ingredient.postIngredient(data),
        onSuccess: ({ data }) => {
            formContext.reset();
            notifications.show(`Created ${data?.name}!`, {
                severity: 'success',
            });
            onClose();
            onSuccess();
        },
    });

    const onSubmit = async (values: Ingredient.CreateRequest) =>
        mutateCreateIngredient.mutateAsync(values).catch(e => {
            notifications.show('Error creating ingredient!', {
                severity: 'error',
            });
        });

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create Ingredient</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent>
                    <Stack gap={2}>
                        <TextFieldElement label="Name" name="name" fullWidth />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                        />
                        <SelectElement
                            label="Category"
                            name="category"
                            fullWidth
                            options={enumToFormOptions(INGREDIENT_CATEGORIES)}
                        />
                        <CheckboxElement label="Is Alcohol" name="isAlcohol" />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
